import { useTranslation } from 'react-i18next';
import { CheckSquare, Square } from 'lucide-react';
import type { PerioExamListItem } from '@/hooks/usePerioExams';

// ── Props ─────────────────────────────────────────────────────────────────────

interface PerioTimelineProps {
  exams: PerioExamListItem[];
  activeExamId: string | null;
  compareExamIds: string[];
  onSelectExam: (examId: string) => void;
  onToggleCompare: (examId: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PerioTimeline({
  exams,
  activeExamId,
  compareExamIds,
  onSelectExam,
  onToggleCompare,
}: PerioTimelineProps) {
  const { t } = useTranslation();

  if (exams.length === 0) {
    return (
      <div className="rounded-lg border border-ds-line bg-ds-card p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
          {t('dental.perio.timeline.title')}
        </p>
        <p className="mt-3 text-sm text-ds-placeholder">
          {t('dental.perio.newExam', 'Ingen undersøgelser endnu.')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-ds-line bg-ds-card">
      <div className="border-b border-ds-line px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
          {t('dental.perio.timeline.title')}
        </p>
      </div>

      <ul className="divide-y divide-ds-line">
        {exams.map((exam, idx) => {
          const isActive = exam.exam_id === activeExamId;
          const isCompareSelected = compareExamIds.includes(exam.exam_id);
          const date = new Date(exam.recorded_at).toLocaleDateString('da-DK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });

          return (
            <li key={exam.exam_id} className={`${isActive ? 'bg-ds-teal-tint' : ''}`}>
              <div className="flex items-center gap-2 px-4 py-3">
                {/* Compare toggle */}
                <button
                  type="button"
                  title={t('dental.perio.timeline.compareSelect')}
                  onClick={() => onToggleCompare(exam.exam_id)}
                  className="shrink-0 text-ds-muted hover:text-ds-teal"
                >
                  {isCompareSelected ? (
                    <CheckSquare className="h-4 w-4 text-ds-teal" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>

                {/* Exam details button */}
                <button
                  type="button"
                  onClick={() => onSelectExam(exam.exam_id)}
                  className="flex min-w-0 flex-1 flex-col items-start text-left"
                >
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-sm font-semibold text-ds-text">
                      {t('dental.perio.examLabel', { n: exams.length - idx, defaultValue: `Undersøgelse #${exams.length - idx}` })}
                    </span>
                    <span className="text-xs text-ds-muted">{date}</span>
                  </div>

                  <div className="mt-0.5 flex items-center gap-2">
                    {exam.recorded_by_name && (
                      <span className="text-xs text-ds-muted">
                        {exam.recorded_by_name}
                      </span>
                    )}
                    {exam.worst_pd_mm !== null && (
                      <span className="rounded-full bg-ds-neutral-tint px-1.5 py-0.5 text-xs text-ds-muted bg-ds-card">
                        {t('dental.perio.fields.pd', 'PD')} max {exam.worst_pd_mm} mm
                      </span>
                    )}
                    <span className="text-xs text-ds-placeholder">
                      {exam.measurement_count} mål.
                    </span>
                  </div>
                </button>

                {isActive && (
                  <span className="shrink-0 rounded-full bg-ds-teal-tint px-1.5 py-0.5 text-xs font-medium text-ds-teal">
                    Aktiv
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
